import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import JourneyEntryDetail, { CompareView } from "@/components/gauge/JourneyEntryDetail";
import { Link } from "react-router-dom";
import {
  Loader2, Compass, Heart, Eye, Users, Sprout, Zap, ArrowRight, RotateCcw,
  TrendingUp, TrendingDown, Minus, CalendarClock, Sparkles,
} from "lucide-react";
import api from "@/lib/api";

const ORIENTATION = {
  gratitude: { icon: Heart, color: "#D98E73", label: "Gratitude" },
  awareness: { icon: Eye, color: "#144F75", label: "Awareness" },
  unity: { icon: Users, color: "#8FA89B", label: "Unity" },
  growth: { icon: Sprout, color: "#5B8A72", label: "Growth" },
  energy: { icon: Zap, color: "#B5794E", label: "Energy" },
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso?.slice(0, 10) || ""; }
};
const daysAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

export default function GaugeJourney() {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["my-gauge-journey"],
    queryFn: () => api.get("/me/gauge/explorations").then((r) => r.data),
  });
  const [compare, setCompare] = useState([]);
  const toggleCompare = (id) => setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id].slice(-2)));

  if (isLoading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={32} /></div>;
  }

  // ---- Empty state ----
  if (entries.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="gauge-journey-empty">
        <Compass className="mx-auto text-[#D98E73] mb-4" size={40} />
        <h1 className="font-serif text-3xl text-[#1A242B]">Your GAUGE journey starts here</h1>
        <p className="text-[#4A5D6B] mt-3">Take your first exploration and it will be saved privately here, so you can watch how you change over time.</p>
        <Link to="/gauge/app" data-testid="gauge-journey-start" className="eyw-btn mt-6 inline-flex"><Compass size={16} /> Start GAUGE Exploration</Link>
      </div>
    );
  }

  // entries come newest-first
  const latest = entries[0];
  const previous = entries[1];
  const since = daysAgo(latest.created_at);
  const dueForRetake = since >= 7;

  // orientation frequency
  const counts = {};
  entries.forEach((e) => { counts[e.primary_orientation] = (counts[e.primary_orientation] || 0) + 1; });
  const freq = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const topOrientation = freq[0]?.[0];

  // score trend (oldest -> newest for the sparkline)
  const chrono = [...entries].reverse();
  const scoreDelta = previous ? (latest.score - previous.score) : 0;

  const lo = ORIENTATION[latest.primary_orientation] || ORIENTATION.awareness;
  const LoIcon = lo.icon;

  return (
    <div data-testid="gauge-journey" className="min-h-[70vh]">
      {/* Hero */}
      <section className="relative py-16 bg-[#144F75] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-4xl mx-auto px-6">
          <p className="eyw-label !text-[#D98E73] mb-2">GAUGE · Your journey over time</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight">How you're changing</h1>
          <p className="text-white/80 text-lg mt-4 max-w-xl">A private record of your reflections. This is a mirror, not a measurement — notice what shifts, gently.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        {/* Plain-language insight — a gentle mirror, computed from the member's own pattern */}
        {(() => {
          const topLabel = (ORIENTATION[topOrientation] || {}).label || "";
          const topPct = topOrientation ? Math.round((counts[topOrientation] / entries.length) * 100) : 0;
          const trendLine = !previous ? "This is your first reflection — a lovely place to begin noticing."
            : scoreDelta > 0 ? `Lately that pull feels a little stronger than last time — worth leaning in with some kindness.`
            : scoreDelta < 0 ? `and it has eased a little since your last check-in — notice what's been supporting you.`
            : `and it's held fairly steady since last time.`;
          const patternLine = entries.length > 1 && topLabel
            ? `${topLabel} keeps rising to the surface — it's shown up in ${topPct}% of your reflections${scoreDelta === 0 ? ", " : " "}${trendLine}`
            : trendLine;
          return (
            <div className="rounded-2xl bg-white border-l-4 border-[#D98E73] shadow-sm p-6 mb-8" data-testid="gauge-journey-insight">
              <p className="eyw-label !text-[#D98E73] mb-2 flex items-center gap-1.5"><Sparkles size={13} /> What we're noticing</p>
              <p className="text-[#1A242B] text-lg leading-relaxed">
                You've checked in {entries.length} {entries.length === 1 ? "time" : "times"}. {patternLine}
              </p>
              <p className="text-[#8FA89B] text-sm mt-2">This is a reflection, not a diagnosis — however it moves, simply noticing is the practice.</p>
            </div>
          );
        })()}

        {/* Weekly retake nudge */}
        <div className={`rounded-2xl p-6 mb-8 flex flex-wrap items-center justify-between gap-4 ${dueForRetake ? "bg-[#D98E73]/[0.10] border border-[#D98E73]/30" : "bg-[#8FA89B]/[0.10] border border-[#8FA89B]/25"}`} data-testid="gauge-journey-nudge">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-xl text-white shrink-0" style={{ background: dueForRetake ? "#D98E73" : "#8FA89B" }}><CalendarClock size={20} /></span>
            <div>
              <p className="font-serif text-lg text-[#1A242B]">
                {dueForRetake ? `It's been ${since} day${since === 1 ? "" : "s"} — time for a fresh check-in` : `Last explored ${since === 0 ? "today" : `${since} day${since === 1 ? "" : "s"} ago`}`}
              </p>
              <p className="text-[#4A5D6B] text-sm">A weekly GAUGE helps you see the pattern, not just the moment.</p>
            </div>
          </div>
          <Link to="/gauge/app" data-testid="gauge-journey-retake" className="eyw-btn whitespace-nowrap"><RotateCcw size={16} /> Take GAUGE again</Link>
        </div>

        {/* Summary tiles */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="eyw-card p-5" data-testid="gauge-journey-total">
            <p className="eyw-label mb-1">Explorations</p>
            <p className="font-serif text-4xl text-[#144F75]">{entries.length}</p>
          </div>
          <div className="eyw-card p-5" data-testid="gauge-journey-current">
            <p className="eyw-label mb-1">Most recent focus</p>
            <p className="font-serif text-2xl flex items-center gap-2" style={{ color: lo.color }}><LoIcon size={22} /> {lo.label}</p>
            <p className="text-[#8FA89B] text-xs mt-1">{fmtDate(latest.created_at)}</p>
          </div>
          <div className="eyw-card p-5" data-testid="gauge-journey-trend">
            <p className="eyw-label mb-1">Since last time</p>
            {previous ? (
              <p className="font-serif text-2xl flex items-center gap-2 text-[#1A242B]">
                {scoreDelta > 0 ? <TrendingUp className="text-[#5B8A72]" size={22} /> : scoreDelta < 0 ? <TrendingDown className="text-[#B5794E]" size={22} /> : <Minus className="text-[#8FA89B]" size={22} />}
                {scoreDelta > 0 ? `+${scoreDelta}` : scoreDelta}
              </p>
            ) : <p className="font-serif text-2xl text-[#8FA89B]">First one</p>}
            <p className="text-[#8FA89B] text-xs mt-1">Reflection intensity</p>
          </div>
        </div>

        {/* Orientation frequency */}
        <div className="mb-10" data-testid="gauge-journey-orientations">
          <p className="eyw-label mb-3">What keeps coming up</p>
          <div className="space-y-2.5">
            {freq.map(([key, n]) => {
              const meta = ORIENTATION[key] || ORIENTATION.awareness;
              const Icon = meta.icon;
              const pct = Math.round((n / entries.length) * 100);
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-28 flex items-center gap-1.5 text-sm text-[#4A5D6B] shrink-0" style={{ color: meta.color }}><Icon size={15} /> {meta.label}</span>
                  <div className="flex-1 h-3 rounded-full bg-[#E5E0D8] overflow-hidden">
                    <span className="block h-full rounded-full transition-all" style={{ width: `${pct}%`, background: meta.color }} />
                  </div>
                  <span className="text-xs text-[#8FA89B] w-14 text-right">{n}× · {pct}%</span>
                </div>
              );
            })}
          </div>
          {topOrientation && (
            <p className="text-[#4A5D6B] text-sm mt-4 flex items-start gap-2">
              <Sparkles size={15} className="text-[#D98E73] mt-0.5 shrink-0" />
              <span><strong className="text-[#144F75]">{(ORIENTATION[topOrientation] || {}).label}</strong> has been asking for your attention most often. That's simply a place worth tending — not a problem to fix.</span>
            </p>
          )}
        </div>

        {/* Score sparkline (chronological) */}
        {entries.length > 1 && (
          <div className="mb-10" data-testid="gauge-journey-sparkline">
            <p className="eyw-label mb-1">Over time</p>
            <p className="text-xs text-[#8FA89B] mb-3">A subjective check-in: how often your answers leaned toward feeling supported. It isn't a measure of you or your wellness.</p>
            <div className="eyw-card p-5">
              <div className="flex items-end gap-1.5 h-28">
                {chrono.map((e, i) => {
                  const meta = ORIENTATION[e.primary_orientation] || ORIENTATION.awareness;
                  return (
                    <div key={e.id || i} className="flex-1 h-full flex flex-col justify-end items-center gap-1 group" title={`${fmtDate(e.created_at)} · ${e.score}`}>
                      <span className="w-full rounded-t-md transition-all group-hover:opacity-80" style={{ height: `${Math.max(6, e.score)}%`, background: meta.color }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-[#8FA89B]">
                <span>{fmtDate(chrono[0].created_at)}</span>
                <span>{fmtDate(chrono[chrono.length - 1].created_at)}</span>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {compare.length === 2 && (() => {
          const [a, b] = [...compare].map((id) => entries.find((e) => e.id === id)).sort((x, y) => (x.created_at < y.created_at ? 1 : -1));
          return <CompareView a={a} b={b} />;
        })()}
        <p className="eyw-label mb-1">Your reflections</p>
        <p className="text-xs text-[#8FA89B] mb-3">Every exploration is kept with its date. Tick two to compare then and now.</p>
        <div className="space-y-3" data-testid="gauge-journey-history">
          {entries.map((e) => {
            const meta = ORIENTATION[e.primary_orientation] || ORIENTATION.awareness;
            const Icon = meta.icon;
            return (
              <div key={e.id} className="eyw-card p-5" data-testid={`gauge-journey-entry-${e.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-xl text-white shrink-0" style={{ background: meta.color }}><Icon size={18} /></span>
                    <div>
                      <h3 className="font-serif text-lg text-[#1A242B]">{e.result_title || meta.label}</h3>
                      <p className="text-[#8FA89B] text-xs">{fmtDate(e.created_at)} · {e.path_title}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-[#4A5D6B] flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={compare.includes(e.id)} onChange={() => toggleCompare(e.id)} data-testid={`journey-compare-${e.id}`} /> Compare
                    </label>
                    <span className="text-xs font-medium px-3 py-1 rounded-full shrink-0" style={{ background: `${meta.color}18`, color: meta.color }}>{meta.label}</span>
                  </div>
                </div>
                {e.summary && <p className="text-[#4A5D6B] text-sm mt-3 leading-relaxed">{e.summary}</p>}
                {e.top_focus_areas?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {e.top_focus_areas.map((t) => (
                      <span key={t} className="text-xs bg-[#144F75]/[0.06] text-[#144F75] px-2.5 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                )}
                {e.member_reflection?.chosen_next_step && <p className="text-sm text-[#144F75] mt-3"><b>My next step:</b> {e.member_reflection.chosen_next_step}</p>}
                <JourneyEntryDetail entry={e} />
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/gauge/app" className="eyw-btn"><Compass size={16} /> Explore again <ArrowRight size={15} /></Link>
          <Link to="/coach" className="eyw-btn-outline">Open Daily Coach</Link>
        </div>
      </section>
    </div>
  );
}
